import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "@/firebase";

type InventoryItem = {
  id: string;
  name: string;
  category: string;
  sortNo: number;
  stock: number;
  alert: number;
  stockup: number | null;
  stockupDate: Timestamp | null;
};

const CATEGORY_META: Record<
  string,
  { subtitle: string; icon: string; color: string }
> = {
  "資格": {
    subtitle: "QUALIFICATION",
    icon: "ri-book-mark-line",
    color: "bg-blue-50 text-blue-600",
  },
  "バリエーション": {
    subtitle: "VARIATION",
    icon: "ri-stack-line",
    color: "bg-amber-50 text-amber-600",
  },
  "認定書": {
    subtitle: "CERTIFICATE",
    icon: "ri-award-line",
    color: "bg-emerald-50 text-emerald-600",
  },
  "その他": {
    subtitle: "OTHERS",
    icon: "ri-folder-3-line",
    color: "bg-purple-50 text-purple-600",
  },
};

const CATEGORY_OPTIONS = Object.keys(CATEGORY_META);

function StatusBadge({ status }: { status: string }) {
  const styles =
    status === "Critical"
      ? "bg-red-50 text-red-600"
      : status === "Low Stock"
        ? "bg-amber-50 text-amber-600"
        : "bg-emerald-50 text-emerald-600";

  const dotColor =
    status === "Critical"
      ? "bg-red-500"
      : status === "Low Stock"
        ? "bg-amber-500"
        : "bg-emerald-500";

  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${styles}`}>
      <span className={`w-2 h-2 rounded-full ${dotColor}`} />
      {status}
    </span>
  );
}

function NumberField({
  value,
  onChange,
  onCommit,
  label,
}: {
  value: number;
  onChange: (next: number) => void;
  onCommit: (next: number) => void;
  label: string;
}) {
  return (
    <input
      type="number"
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      value={Number.isFinite(value) ? value : ""}
      aria-label={label}
      onChange={(event) => onChange(Number(event.target.value))}
      onBlur={(event) => onCommit(Number(event.target.value))}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          (event.currentTarget as HTMLInputElement).blur();
        }
      }}
    />
  );
}

export default function InventoryManagementPage() {
  const [inventories, setInventories] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPending, setCreatePending] = useState(false);
  const [newInventory, setNewInventory] = useState({
    name: "",
    category: CATEGORY_OPTIONS[0],
    sortNo: 0,
    stock: 0,
    alert: 0,
  });

  useEffect(() => {
    const inventoriesQuery = query(
      collection(db, "Inventories"),
      orderBy("sortNo", "asc"),
    );

    const unsubscribe = onSnapshot(
      inventoriesQuery,
      (snapshot) => {
        const fetched = snapshot.docs.map((inventoryDoc) => {
          const data = inventoryDoc.data();
          return {
            id: inventoryDoc.id,
            name: data.name ?? "",
            category: data.category ?? "その他",
            sortNo: Number(data.sortNo ?? 0),
            stock: Number(data.stock ?? 0),
            alert: Number(data.alert ?? 0),
            stockup: Number.isFinite(data.Stockup) ? Number(data.Stockup) : null,
            stockupDate: data.StockupDate ?? null,
          } satisfies InventoryItem;
        });

        setInventories(fetched);
        setLoading(false);
      },
      (snapshotError) => {
        console.error(snapshotError);
        setError("在庫データの取得に失敗しました。時間をおいて再度お試しください。");
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const categorySummaries = useMemo(() => {
    const counts = new Map<string, number>();

    inventories.forEach((item) => {
      counts.set(item.category, (counts.get(item.category) ?? 0) + 1);
    });

    const knownCategories = Object.keys(CATEGORY_META);
    const summaries = knownCategories.map((category) => ({
      category,
      count: counts.get(category) ?? 0,
      meta: CATEGORY_META[category],
    }));

    counts.forEach((count, category) => {
      if (!knownCategories.includes(category)) {
        summaries.push({
          category,
          count,
          meta: {
            subtitle: "OTHERS",
            icon: "ri-archive-line",
            color: "bg-gray-50 text-gray-600",
          },
        });
      }
    });

    return summaries;
  }, [inventories]);

  const lowStockAlerts = useMemo(
    () => inventories.filter((item) => item.stock < item.alert),
    [inventories],
  );

  const filteredInventories = useMemo(() => {
    if (!selectedCategory) return inventories;
    return inventories.filter((item) => item.category === selectedCategory);
  }, [inventories, selectedCategory]);

  const handleUpdateField = async (
    id: string,
    field: keyof Pick<InventoryItem, "stock" | "alert">,
    value: number,
  ) => {
    if (!Number.isFinite(value)) {
      return;
    }

    try {
      setSavingField(`${id}-${field}`);
      await updateDoc(doc(db, "Inventories", id), { [field]: value });
      setError(null);
    } catch (updateError) {
      console.error(updateError);
      setError("更新に失敗しました。もう一度お試しください。");
    } finally {
      setSavingField(null);
    }
  };

  const handleUpdateStockup = async (id: string, value: number | null) => {
    if (value !== null && !Number.isFinite(value)) {
      return;
    }

    try {
      setSavingField(`${id}-stockup`);
      await updateDoc(doc(db, "Inventories", id), { Stockup: value });
      setError(null);
    } catch (updateError) {
      console.error(updateError);
      setError("更新に失敗しました。もう一度お試しください。");
    } finally {
      setSavingField(null);
    }
  };

  const handleUpdateStockupDate = async (id: string, value: Timestamp | null) => {
    try {
      setSavingField(`${id}-stockupDate`);
      await updateDoc(doc(db, "Inventories", id), { StockupDate: value });
      setError(null);
    } catch (updateError) {
      console.error(updateError);
      setError("更新に失敗しました。もう一度お試しください。");
    } finally {
      setSavingField(null);
    }
  };

  const handleReceiveStockup = async (item: InventoryItem) => {
    if (item.stockup === null || !Number.isFinite(item.stockup)) {
      return;
    }

    const nextStock = item.stock + item.stockup;
    try {
      setSavingField(`${item.id}-receive`);
      await updateDoc(doc(db, "Inventories", item.id), {
        stock: nextStock,
        Stockup: null,
        StockupDate: null,
      });
      setInventories((prev) =>
        prev.map((inventory) =>
          inventory.id === item.id
            ? { ...inventory, stock: nextStock, stockup: null, stockupDate: null }
            : inventory,
        ),
      );
      setError(null);
    } catch (updateError) {
      console.error(updateError);
      setError("更新に失敗しました。もう一度お試しください。");
    } finally {
      setSavingField(null);
    }
  };

  const formatDateInputValue = (value: Timestamp | null) => {
    if (!value) return "";
    return value.toDate().toISOString().slice(0, 10);
  };

  const statusLabel = (item: InventoryItem) => {
    if (item.stock === 0) return "Critical";
    if (item.stock < item.alert) return "Low Stock";
    return "In Stock";
  };

  const resetCreateForm = () => {
    setNewInventory({
      name: "",
      category: CATEGORY_OPTIONS[0],
      sortNo: 0,
      stock: 0,
      alert: 0,
    });
  };

  const handleCreateInventory = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!newInventory.name.trim()) {
      setError("在庫名を入力してください。");
      return;
    }

    setCreatePending(true);
    try {
      await addDoc(collection(db, "Inventories"), {
        name: newInventory.name.trim(),
        category: newInventory.category,
        sortNo: Number(newInventory.sortNo),
        stock: Number(newInventory.stock),
        alert: Number(newInventory.alert),
      });

      setError(null);
      setShowCreateModal(false);
      resetCreateForm();
    } catch (creationError) {
      console.error(creationError);
      setError("在庫の追加に失敗しました。もう一度お試しください。");
    } finally {
      setCreatePending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">
            FirestoreのInventoriesコレクションと同期された在庫一覧です。
          </p>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">在庫管理</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg shadow-sm hover:bg-blue-500 transition-colors"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="ri-add-line text-lg" />
            新規追加 (Firestore)
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {categorySummaries.map((category) => (
          <div
            key={category.category}
            role="button"
            tabIndex={0}
            onClick={() =>
              setSelectedCategory((prev) =>
                prev === category.category ? null : category.category,
              )
            }
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setSelectedCategory((prev) =>
                  prev === category.category ? null : category.category,
                );
              }
            }}
            className={`bg-white rounded-2xl border shadow-sm p-5 flex flex-col gap-3 transition-colors cursor-pointer ${selectedCategory === category.category ? "border-blue-200 ring-2 ring-blue-100" : "border-gray-100"}`}
          >
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-gray-500">{category.meta.subtitle}</p>
                <h2 className="text-lg font-semibold text-gray-900">{category.category}</h2>
              </div>
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${category.meta.color}`}
              >
                <i className={category.meta.icon} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-3xl font-bold text-gray-900">{category.count}</p>
              <span className="text-sm font-semibold text-blue-600">件</span>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center text-xl">
              <i className="ri-notification-3-line" />
            </div>
            <div>
              <p className="text-xs font-semibold text-amber-600">アラート在庫の通知 (Low Stock Alerts)</p>
              <p className="text-sm text-gray-600">在庫が少なくなった商品を確認し、補充のアクションを行ってください。</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600">
            <i className="ri-filter-3-line" />
            {lowStockAlerts.length} 件の要確認
          </span>
        </div>

        <div className="divide-y divide-gray-100">
          {lowStockAlerts.length === 0 && (
            <div className="px-5 py-6 text-sm text-gray-500">アラート対象の在庫はありません。</div>
          )}

          {lowStockAlerts.map((alertItem) => (
            <div
              key={alertItem.id}
              className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4 px-5 py-4"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{alertItem.name}</p>
                <p className="text-sm text-gray-500 truncate">カテゴリー: {alertItem.category}</p>
              </div>
              <div className="w-24 font-semibold text-gray-900">{alertItem.stock}</div>
              <div className="w-24 text-sm text-gray-600">アラート: {alertItem.alert}</div>
              <div className="min-w-[120px]">
                <StatusBadge status={statusLabel(alertItem)} />
              </div>
              <div className="min-w-[160px]">
                <label className="block text-xs font-semibold text-gray-500 mb-1">入荷予定日</label>
                <input
                  type="date"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  value={formatDateInputValue(alertItem.stockupDate)}
                  onChange={(event) => {
                    const value = event.target.value;
                    const next = value ? Timestamp.fromDate(new Date(`${value}T00:00:00`)) : null;
                    setInventories((prev) =>
                      prev.map((inventory) =>
                        inventory.id === alertItem.id
                          ? { ...inventory, stockupDate: next }
                          : inventory,
                      ),
                    );
                  }}
                  onBlur={(event) => {
                    const value = event.target.value;
                    const next = value ? Timestamp.fromDate(new Date(`${value}T00:00:00`)) : null;
                    handleUpdateStockupDate(alertItem.id, next);
                  }}
                />
                {savingField === `${alertItem.id}-stockupDate` && (
                  <p className="mt-1 text-[11px] text-blue-600">更新中...</p>
                )}
              </div>
              <div className="min-w-[140px]">
                <label className="block text-xs font-semibold text-gray-500 mb-1">入荷予定数</label>
                <input
                  type="number"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  value={alertItem.stockup ?? ""}
                  onChange={(event) => {
                    const nextValue = event.target.value === "" ? null : Number(event.target.value);
                    setInventories((prev) =>
                      prev.map((inventory) =>
                        inventory.id === alertItem.id
                          ? { ...inventory, stockup: nextValue }
                          : inventory,
                      ),
                    );
                  }}
                  onBlur={(event) => {
                    const nextValue = event.target.value === "" ? null : Number(event.target.value);
                    handleUpdateStockup(alertItem.id, nextValue);
                  }}
                />
                {savingField === `${alertItem.id}-stockup` && (
                  <p className="mt-1 text-[11px] text-blue-600">更新中...</p>
                )}
              </div>
              <div className="min-w-[140px]">
                <button
                  type="button"
                  className={`w-full px-4 py-2 text-sm font-semibold rounded-lg transition-colors border ${
                    alertItem.stockup === null || !Number.isFinite(alertItem.stockup)
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-blue-600 text-white hover:bg-blue-500 border-blue-600"
                  }`}
                  onClick={() => handleReceiveStockup(alertItem)}
                  disabled={alertItem.stockup === null || !Number.isFinite(alertItem.stockup)}
                >
                  入荷済み
                </button>
                {savingField === `${alertItem.id}-receive` && (
                  <p className="mt-1 text-[11px] text-blue-600">更新中...</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-xs font-semibold text-blue-600">STOCK LIST</p>
            <h2 className="text-lg font-semibold text-gray-900">在庫一覧</h2>
            <p className="text-sm text-gray-500">sortNo昇順で表示しています。数値は直接編集できます。</p>
          </div>
          {loading && (
            <div className="inline-flex items-center gap-2 text-sm text-gray-500">
              <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
              読み込み中...
            </div>
          )}
          {!loading && (
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <span className="font-semibold text-gray-900">
                フィルタ: {selectedCategory ?? "すべて"}
              </span>
              {selectedCategory && (
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-500"
                  onClick={() => setSelectedCategory(null)}
                >
                  クリア
                </button>
              )}
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">sortNo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">在庫品名</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">カテゴリー</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">在庫数</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">アラート下限</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">ステータス</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredInventories.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.sortNo}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.category}</td>
                  <td className="px-4 py-3 min-w-[140px]">
                    <NumberField
                      value={item.stock}
                      label={`${item.name} 在庫数`}
                      onChange={(next) =>
                        setInventories((prev) =>
                          prev.map((inventory) =>
                            inventory.id === item.id
                              ? { ...inventory, stock: next }
                              : inventory,
                          ),
                        )
                      }
                      onCommit={(next) => handleUpdateField(item.id, "stock", next)}
                    />
                    {savingField === `${item.id}-stock` && (
                      <p className="mt-1 text-[11px] text-blue-600">更新中...</p>
                    )}
                  </td>
                  <td className="px-4 py-3 min-w-[140px]">
                    <NumberField
                      value={item.alert}
                      label={`${item.name} アラート在庫`}
                      onChange={(next) =>
                        setInventories((prev) =>
                          prev.map((inventory) =>
                            inventory.id === item.id
                              ? { ...inventory, alert: next }
                              : inventory,
                          ),
                        )
                      }
                      onCommit={(next) => handleUpdateField(item.id, "alert", next)}
                    />
                    {savingField === `${item.id}-alert` && (
                      <p className="mt-1 text-[11px] text-blue-600">更新中...</p>
                    )}
                  </td>
                  <td className="px-4 py-3 min-w-[140px]">
                    <StatusBadge status={statusLabel(item)} />
                  </td>
                </tr>
              ))}
              {!loading && filteredInventories.length === 0 && (
                <tr>
                  <td
                    className="px-4 py-6 text-center text-sm text-gray-500"
                    colSpan={6}
                  >
                    該当する在庫がありません。
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showCreateModal && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div>
                <p className="text-xs font-semibold text-blue-600">NEW INVENTORY</p>
                <h3 className="text-lg font-semibold text-gray-900">在庫を追加</h3>
              </div>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setShowCreateModal(false)}
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <form className="space-y-4 p-6" onSubmit={handleCreateInventory}>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-800">name</label>
                <input
                  type="text"
                  required
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  value={newInventory.name}
                  onChange={(event) =>
                    setNewInventory((prev) => ({ ...prev, name: event.target.value }))
                  }
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-800">category</label>
                <select
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  value={newInventory.category}
                  onChange={(event) =>
                    setNewInventory((prev) => ({ ...prev, category: event.target.value }))
                  }
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-800">sortNo</label>
                <input
                  type="number"
                  required
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  value={newInventory.sortNo}
                  onChange={(event) =>
                    setNewInventory((prev) => ({ ...prev, sortNo: Number(event.target.value) }))
                  }
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-800">stock</label>
                <input
                  type="number"
                  required
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  value={newInventory.stock}
                  onChange={(event) =>
                    setNewInventory((prev) => ({ ...prev, stock: Number(event.target.value) }))
                  }
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-800">alert</label>
                <input
                  type="number"
                  required
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  value={newInventory.alert}
                  onChange={(event) =>
                    setNewInventory((prev) => ({ ...prev, alert: Number(event.target.value) }))
                  }
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetCreateForm();
                  }}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-500 disabled:opacity-60"
                  disabled={createPending}
                >
                  {createPending && (
                    <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                  )}
                  追加する
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
